import { computed, getCurrentInstance, onUnmounted, ref } from 'vue';
import WhatsappCallsAPI from 'dashboard/api/whatsappCalls';
import WhatsappCallMedia from 'dashboard/helper/whatsappCallMedia';
import { useWhatsappCallsStore } from 'dashboard/stores/whatsappCalls';

const TERMINAL_STATUSES = new Set(['rejected', 'ended', 'failed', 'missed']);
const QR_POLL_INTERVAL_MS = 2000;
const CALL_STATUS_POLL_INTERVAL_MS = 1500;
const QR_POLL_STATUSES = new Set([
  'awaiting_qr',
  'waiting_qr',
  'pairing',
  'connecting',
  'pending',
]);

const STATUS_ALIASES = {
  created: 'ringing',
  initiated: 'ringing',
  initiating: 'ringing',
  incoming: 'ringing',
  calling: 'ringing',
  answered: 'connecting',
  answering: 'connecting',
  accepted: 'connecting',
  active: 'connected',
  in_progress: 'connected',
  declined: 'rejected',
  busy: 'rejected',
  cancelled: 'ended',
  canceled: 'ended',
  completed: 'ended',
  disconnected: 'ended',
  hangup: 'ended',
  terminated: 'ended',
  error: 'failed',
};

export const getWhatsappConversationPhone = conversation => {
  const sender = conversation?.meta?.sender || {};
  const contact = conversation?.contact || {};
  return (
    sender.phone_number ||
    sender.phoneNumber ||
    contact.phone_number ||
    contact.phoneNumber ||
    conversation?.phone_number ||
    ''
  );
};

export const getWhatsappConnectionId = (conversation, call) =>
  call?.connection_id ||
  call?.connectionId ||
  conversation?.lais_whatsapp_connection_id ||
  conversation?.inbox?.lais_whatsapp_connection_id ||
  conversation?.contact_inbox?.lais_whatsapp_connection_id ||
  conversation?.meta?.lais_whatsapp_connection_id ||
  conversation?.additional_attributes?.whatsapp_call?.connection_id ||
  null;

export const getWhatsappCallControlId = call =>
  call?.gateway_call_id || call?.gatewayCallId || call?.id || null;

const normalizeStatus = status => {
  const normalizedStatus = String(status || '').toLowerCase();
  return STATUS_ALIASES[normalizedStatus] || normalizedStatus;
};

const statusFromMessage = message =>
  message?.status || message?.call?.status || message?.event || message?.type;

const callFromResponse = data => data?.call || data?.data?.call || null;

const connectionFromResponse = data =>
  data?.connection || data?.data?.connection || null;

const errorMessage = error =>
  error?.response?.data?.error ||
  error?.message ||
  'Unable to start WhatsApp call';

const shouldPollForQr = connection => {
  const gatewayStatus = String(
    connection?.call_gateway_status || connection?.status || ''
  ).toLowerCase();
  return (
    connection?.call_gateway_connected !== true &&
    QR_POLL_STATUSES.has(gatewayStatus)
  );
};

export function useWhatsappCall({
  api = WhatsappCallsAPI,
  mediaSession = new WhatsappCallMedia(),
} = {}) {
  const callsStore = useWhatsappCallsStore();
  let qrPollingTimer = null;
  let qrPollingConnectionId = null;
  let qrPollingGeneration = 0;
  let callPollingTimer = null;
  let callPollingCallId = null;
  let callPollingGeneration = 0;
  let gatewayRequestQueue = Promise.resolve();
  let gatewayRequestCount = 0;
  let gatewayRequestGeneration = 0;
  let mediaFailureTerminationCallId = null;
  let connectedMediaCallId = null;
  let connectedMediaToken = null;
  const callLinkResult = ref(null);
  let refreshConnection;
  let scheduleCallPolling;

  const call = computed(() => callsStore.call);
  const status = computed(() => callsStore.status);
  const conversation = computed(() => callsStore.conversation);
  const connections = computed(() => callsStore.connections);
  const connection = computed(() => callsStore.connection);
  const connectionId = computed(() => callsStore.connectionId);
  const isIncoming = computed(() => callsStore.isIncoming);
  const isStarting = computed(() => callsStore.isStarting);
  const isPerformingAction = computed(() => callsStore.isPerformingAction);
  const isLoadingConnections = computed(() => callsStore.isLoadingConnections);
  const isRefreshingConnection = computed(
    () => callsStore.isRefreshingConnection
  );
  const error = computed(() => callsStore.error);
  const mediaError = computed(() => callsStore.mediaError);

  const stopCallPolling = () => {
    if (callPollingTimer) window.clearTimeout(callPollingTimer);
    callPollingTimer = null;
    callPollingCallId = null;
    callPollingGeneration += 1;
  };

  const disconnectMedia = () => {
    connectedMediaCallId = null;
    connectedMediaToken = null;
    mediaSession.disconnect();
  };

  const applyCall = nextCall => {
    if (!nextCall?.id) return null;

    if (String(callsStore.call?.id || '') !== String(nextCall.id)) {
      mediaFailureTerminationCallId = null;
    }

    const mergedCall = { ...callsStore.call, ...nextCall };
    callsStore.setCall(mergedCall);
    const normalizedStatus = normalizeStatus(mergedCall.status) || 'ringing';
    callsStore.setStatus(normalizedStatus);

    const nextConnectionId = getWhatsappConnectionId(
      callsStore.conversation,
      mergedCall
    );
    if (nextConnectionId) callsStore.setConnectionId(nextConnectionId);
    if (TERMINAL_STATUSES.has(normalizedStatus)) {
      stopCallPolling();
      disconnectMedia();
    }

    return mergedCall;
  };

  const applyCallStatus = message => {
    const messageType = String(message?.type || message?.event || '');
    if (messageType === 'call.video_keyframe_requested') {
      mediaSession.requestVideoKeyframe?.();
      return;
    }
    if (messageType.startsWith('call.') && !message?.status) {
      if (!callsStore.call) return;
      const featureCall = { ...callsStore.call };
      if (messageType === 'call.video_state') {
        Object.assign(featureCall, {
          video: message.video,
          sending_video: message.sending_video,
          receiving_video: message.receiving_video,
          video_state: message.video_state,
          video_orientation: message.orientation,
        });
      }
      if (messageType === 'call.group_state') featureCall.group = message.group;
      if (messageType === 'call.waiting_room') {
        featureCall.waiting_room = message.waiting_room;
      }
      if (messageType === 'call.screen_shares') {
        featureCall.screen_shares = message.screen_shares;
      }
      if (messageType === 'call.reaction') {
        featureCall.last_call_feature_event = {
          event: messageType,
          data: message,
        };
      }
      applyCall(featureCall);
      return;
    }
    const nextStatus = normalizeStatus(statusFromMessage(message));
    if (!nextStatus) return;

    if (message?.call) applyCall(message.call);
    else if (message?.muted !== undefined && callsStore.call) {
      applyCall({
        ...callsStore.call,
        status: nextStatus,
        muted: message.muted,
      });
    } else callsStore.setStatus(nextStatus);
    if (message?.connection) callsStore.setConnection(message.connection);

    if (TERMINAL_STATUSES.has(nextStatus)) disconnectMedia();
  };

  const terminateCallAfterMediaFailure = message => {
    callsStore.setMediaError(message);
    const activeCallId = callsStore.call?.id;
    if (
      !activeCallId ||
      !api.performAction ||
      TERMINAL_STATUSES.has(status.value) ||
      String(mediaFailureTerminationCallId) === String(activeCallId)
    ) {
      return;
    }

    mediaFailureTerminationCallId = activeCallId;
    void performAction('end').then(succeeded => {
      if (!succeeded) mediaFailureTerminationCallId = null;
    });
  };

  const connectMedia = nextCall => {
    if (!nextCall?.media_token || !nextCall?.media_path) {
      callsStore.setMediaError('Missing WhatsApp call media credentials');
      terminateCallAfterMediaFailure('Missing WhatsApp call media credentials');
      return false;
    }

    const nextCallId = getWhatsappCallControlId(nextCall);
    if (
      String(connectedMediaCallId) === String(nextCallId) &&
      connectedMediaToken === nextCall.media_token
    ) {
      return true;
    }
    if (connectedMediaCallId || connectedMediaToken) disconnectMedia();
    connectedMediaCallId = nextCallId;
    connectedMediaToken = nextCall.media_token;

    try {
      mediaSession.connect({
        mediaPath: nextCall.media_path,
        token: nextCall.media_token,
        onStatus: applyCallStatus,
        onError: terminateCallAfterMediaFailure,
        onDisconnect: terminateCallAfterMediaFailure,
      });
      return true;
    } catch (connectionError) {
      connectedMediaCallId = null;
      connectedMediaToken = null;
      terminateCallAfterMediaFailure(errorMessage(connectionError));
      return false;
    }
  };

  const stopQrPolling = () => {
    if (qrPollingTimer) window.clearTimeout(qrPollingTimer);
    qrPollingTimer = null;
    qrPollingConnectionId = null;
    qrPollingGeneration += 1;
  };

  const scheduleQrPolling = gatewayConnectionId => {
    if (!gatewayConnectionId) return;

    if (
      qrPollingConnectionId &&
      String(qrPollingConnectionId) !== String(gatewayConnectionId)
    ) {
      stopQrPolling();
    }

    if (!shouldPollForQr(callsStore.connection)) {
      stopQrPolling();
      return;
    }
    if (qrPollingTimer) return;

    qrPollingConnectionId = gatewayConnectionId;
    const pollingGeneration = qrPollingGeneration;
    qrPollingTimer = window.setTimeout(async () => {
      qrPollingTimer = null;
      if (
        pollingGeneration !== qrPollingGeneration ||
        String(qrPollingConnectionId) !== String(gatewayConnectionId)
      ) {
        return;
      }

      await refreshConnection(qrPollingConnectionId, { schedulePoll: false });

      if (
        pollingGeneration === qrPollingGeneration &&
        String(qrPollingConnectionId) === String(gatewayConnectionId) &&
        shouldPollForQr(callsStore.connection)
      ) {
        scheduleQrPolling(qrPollingConnectionId);
      } else {
        stopQrPolling();
      }
    }, QR_POLL_INTERVAL_MS);
  };

  const reconcileQrPolling = connectionToCheck => {
    const nextConnectionId = connectionToCheck?.id || callsStore.connectionId;
    if (shouldPollForQr(connectionToCheck) && nextConnectionId) {
      scheduleQrPolling(nextConnectionId);
    } else {
      stopQrPolling();
    }
  };

  const queueGatewayRequest = request => {
    const requestGeneration = gatewayRequestGeneration;
    gatewayRequestCount += 1;
    callsStore.isRefreshingConnection = true;

    const queuedRequest = gatewayRequestQueue.then(
      () => request(requestGeneration),
      () => request(requestGeneration)
    );
    gatewayRequestQueue = queuedRequest.catch(() => undefined);

    return queuedRequest.finally(() => {
      gatewayRequestCount -= 1;
      if (
        requestGeneration === gatewayRequestGeneration &&
        gatewayRequestCount === 0
      ) {
        callsStore.isRefreshingConnection = false;
      }
    });
  };

  refreshConnection = async (
    gatewayConnectionId,
    { schedulePoll = true } = {}
  ) => {
    if (!gatewayConnectionId) return null;

    callsStore.setConnectionId(gatewayConnectionId);
    return queueGatewayRequest(async requestGeneration => {
      try {
        const { data } = await api.getCallGateway(gatewayConnectionId);
        const nextConnection = connectionFromResponse(data);
        if (
          nextConnection &&
          requestGeneration === gatewayRequestGeneration &&
          String(callsStore.connectionId) === String(gatewayConnectionId)
        ) {
          callsStore.setConnection(nextConnection);
          if (schedulePoll) reconcileQrPolling(nextConnection);
        }
        return nextConnection;
      } catch (requestError) {
        if (requestGeneration === gatewayRequestGeneration) {
          callsStore.setMediaError(errorMessage(requestError));
        }
        return null;
      }
    });
  };

  const updateConnection = async (gatewayConnectionId, payload = {}) => {
    if (!gatewayConnectionId) return null;

    stopQrPolling();
    callsStore.setConnectionId(gatewayConnectionId);
    return queueGatewayRequest(async requestGeneration => {
      try {
        const { data } = await api.updateCallGateway(
          gatewayConnectionId,
          payload
        );
        const nextConnection = connectionFromResponse(data);
        if (
          nextConnection &&
          requestGeneration === gatewayRequestGeneration &&
          String(callsStore.connectionId) === String(gatewayConnectionId)
        ) {
          callsStore.setConnection(nextConnection);
          reconcileQrPolling(nextConnection);
        }
        return nextConnection;
      } catch (requestError) {
        const nextConnection = connectionFromResponse(
          requestError?.response?.data
        );
        if (
          nextConnection &&
          requestGeneration === gatewayRequestGeneration &&
          String(callsStore.connectionId) === String(gatewayConnectionId)
        ) {
          callsStore.setConnection(nextConnection);
          reconcileQrPolling(nextConnection);
        }
        if (requestGeneration === gatewayRequestGeneration) {
          callsStore.setMediaError(errorMessage(requestError));
        }
        return nextConnection;
      }
    });
  };

  const loadConnections = async nextConversation => {
    if (!nextConversation?.inbox_id) {
      callsStore.setConnections([]);
      return [];
    }

    callsStore.isLoadingConnections = true;
    try {
      const { data } = await api.listConnections();
      const activeConnections = (data?.data || []).filter(
        candidateConnection => {
          const candidateInboxId =
            candidateConnection.inbox_id ?? candidateConnection.inbox?.id;

          return (
            String(candidateInboxId) === String(nextConversation.inbox_id) &&
            (candidateConnection.active ||
              candidateConnection.status === 'open')
          );
        }
      );
      callsStore.setConnections(activeConnections);
      return activeConnections;
    } catch (requestError) {
      callsStore.setConnections([]);
      callsStore.setError(errorMessage(requestError));
      return [];
    } finally {
      callsStore.isLoadingConnections = false;
    }
  };

  const startCall = async (
    nextConversation,
    selectedConnectionId,
    options = {}
  ) => {
    const { video = false } = options;
    const hasGroupTarget =
      options.groupId ||
      (Array.isArray(options.targets) && options.targets.length);
    const hasCallLink = Boolean(options.callLink);
    if (
      !nextConversation?.id ||
      (!getWhatsappConversationPhone(nextConversation) &&
        !hasGroupTarget &&
        !hasCallLink)
    ) {
      callsStore.setError(
        'WhatsApp call requires a conversation and callable target'
      );
      return null;
    }
    if (!selectedConnectionId) {
      callsStore.setError('WhatsApp call connection is required');
      return null;
    }

    callsStore.setConversation(nextConversation);
    callsStore.setConnectionId(selectedConnectionId);
    callsStore.isStarting = true;
    callsStore.setError('');
    try {
      await mediaSession.prepareMicrophone?.();
      if (video) await mediaSession.startVideoCapture?.();

      const startOptions = {
        video,
        groupId: options.groupId,
        targets: options.targets,
        callLink: options.callLink,
      };
      const { data } = Object.values(startOptions).some(Boolean)
        ? await api.start(
            nextConversation.id,
            selectedConnectionId,
            undefined,
            startOptions
          )
        : await api.start(nextConversation.id, selectedConnectionId);
      const nextCall = applyCall(callFromResponse(data));
      if (!nextCall) throw new Error('Invalid WhatsApp call response');

      const nextConnection = connectionFromResponse(data);
      if (nextConnection) callsStore.setConnection(nextConnection);
      else await refreshConnection(selectedConnectionId);

      connectMedia(nextCall);
      scheduleCallPolling(nextCall.id);
      return nextCall;
    } catch (requestError) {
      const nextConnection = connectionFromResponse(
        requestError?.response?.data
      );
      if (nextConnection) {
        callsStore.setConnectionId(nextConnection.id);
        callsStore.setConnection(nextConnection);
        reconcileQrPolling(nextConnection);
      }
      callsStore.setError(errorMessage(requestError));
      mediaSession.disconnect();
      return null;
    } finally {
      callsStore.isStarting = false;
    }
  };

  const createCallLink = async (
    selectedConnectionId,
    { video = false } = {}
  ) => {
    if (!selectedConnectionId || !api.callLink) return null;
    callsStore.setError('');
    try {
      const { data } = await api.callLink(selectedConnectionId, 'create', {
        video,
      });
      callLinkResult.value = data?.data || data;
      return callLinkResult.value;
    } catch (requestError) {
      callsStore.setError(errorMessage(requestError));
      return null;
    }
  };

  const previewCallLink = async (
    selectedConnectionId,
    link,
    { video = false } = {}
  ) => {
    if (!selectedConnectionId || !link?.trim() || !api.callLink) return null;
    callsStore.setError('');
    try {
      const { data } = await api.callLink(selectedConnectionId, 'preview', {
        link: link.trim(),
        video,
      });
      callLinkResult.value = data?.data || data;
      return callLinkResult.value;
    } catch (requestError) {
      callsStore.setError(errorMessage(requestError));
      return null;
    }
  };

  const joinCallLink = (
    nextConversation,
    selectedConnectionId,
    link,
    { video = false } = {}
  ) =>
    startCall(nextConversation, selectedConnectionId, {
      video,
      callLink: link?.trim(),
    });

  const hydrateIncomingCall = async callId => {
    if (!callId || !api.getCall) return null;

    try {
      const { data } = await api.getCall(callId);
      const hydratedCall = callFromResponse(data);
      if (!hydratedCall?.id || String(hydratedCall.id) !== String(callId)) {
        return null;
      }

      const nextCall = applyCall(hydratedCall);
      const nextConnection = connectionFromResponse(data);
      if (nextConnection) callsStore.setConnection(nextConnection);
      if (normalizeStatus(nextCall.status) === 'connected') {
        connectMedia(nextCall);
      }
      return nextCall;
    } catch (requestError) {
      callsStore.setMediaError(errorMessage(requestError));
      return null;
    }
  };

  const refreshOutboundCall = async callId => {
    if (!callId || !api.getCall) return null;

    try {
      const { data } = await api.getCall(callId);
      const refreshedCall = callFromResponse(data);
      if (!refreshedCall?.id || String(refreshedCall.id) !== String(callId)) {
        return null;
      }

      const nextCall = applyCall(refreshedCall);
      const nextConnection = connectionFromResponse(data);
      if (nextConnection) callsStore.setConnection(nextConnection);
      if (!TERMINAL_STATUSES.has(normalizeStatus(nextCall.status))) {
        scheduleCallPolling(callId);
      }
      return nextCall;
    } catch {
      scheduleCallPolling(callId);
      return null;
    }
  };

  scheduleCallPolling = callId => {
    if (!callId || !api.getCall || TERMINAL_STATUSES.has(status.value)) {
      stopCallPolling();
      return;
    }

    if (callPollingCallId && String(callPollingCallId) !== String(callId)) {
      stopCallPolling();
    }
    if (callPollingTimer) return;

    callPollingCallId = callId;
    const pollingGeneration = callPollingGeneration;
    callPollingTimer = window.setTimeout(async () => {
      callPollingTimer = null;
      if (
        pollingGeneration !== callPollingGeneration ||
        String(callPollingCallId) !== String(callId)
      ) {
        return;
      }

      await refreshOutboundCall(callId);
    }, CALL_STATUS_POLL_INTERVAL_MS);
  };

  const receiveIncomingCall = ({
    accountId,
    call: incomingCall,
    conversation: incomingConversation,
    connection: incomingConnection,
  } = {}) => {
    if (!incomingCall?.id) return false;

    if (
      callsStore.accountId &&
      accountId &&
      String(callsStore.accountId) !== String(accountId)
    ) {
      callsStore.reset();
    }

    callsStore.setAccountId(
      accountId || incomingConversation?.account_id || null
    );
    callsStore.setConversation(incomingConversation || callsStore.conversation);
    callsStore.setIncoming(incomingCall.direction === 'inbound');
    const nextCall = applyCall(incomingCall);
    if (incomingConnection) callsStore.setConnection(incomingConnection);

    const nextConnectionId = getWhatsappConnectionId(
      incomingConversation,
      nextCall
    );
    if (nextConnectionId && !incomingConnection) {
      refreshConnection(nextConnectionId);
    }
    hydrateIncomingCall(incomingCall.id);
    scheduleCallPolling(incomingCall.id);

    return true;
  };

  const sendControl = (action, payload) => {
    const controlId = getWhatsappCallControlId(call.value);
    if (!controlId) return false;
    if (payload === undefined)
      return mediaSession.sendControl(action, controlId);

    return mediaSession.sendControl(action, controlId, payload);
  };

  const performAction = async (action, payload) => {
    const callId = call.value?.id;
    if (!callId || !api.performAction) return sendControl(action, payload);

    callsStore.isPerformingAction = true;
    callsStore.setError('');
    try {
      if (action === 'answer') {
        await mediaSession.prepareMicrophone?.();
      }
      const response =
        payload === undefined
          ? await api.performAction(callId, action)
          : await api.performAction(callId, action, payload);
      const { data } = response;
      const nextCall = applyCall(callFromResponse(data));
      if (!nextCall) throw new Error('Invalid WhatsApp call action response');

      const nextConnection = connectionFromResponse(data);
      if (nextConnection) callsStore.setConnection(nextConnection);

      if (action === 'answer') connectMedia(nextCall);
      if (TERMINAL_STATUSES.has(status.value)) disconnectMedia();
      return true;
    } catch (requestError) {
      callsStore.setError(errorMessage(requestError));
      return false;
    } finally {
      callsStore.isPerformingAction = false;
    }
  };

  const answerCall = () => performAction('answer');
  const rejectCall = () => performAction('reject');
  const endCall = () => performAction('end');
  const setMuted = muted => performAction('mute', { muted });
  const startVideo = async () => {
    try {
      await mediaSession.startVideoCapture?.();
    } catch (captureError) {
      callsStore.setMediaError(errorMessage(captureError));
      return false;
    }
    const started = await performAction('start_video');
    if (!started) await mediaSession.stopVideoCapture?.();
    return started;
  };
  const acceptVideo = () => performAction('accept_video');
  const stopVideo = async () => {
    const stopped = await performAction('stop_video');
    if (stopped) await mediaSession.stopVideoCapture?.();
    return stopped;
  };
  const setVideoEnabled = async enabled => {
    if (enabled) {
      try {
        await mediaSession.startVideoCapture?.();
      } catch (captureError) {
        callsStore.setMediaError(errorMessage(captureError));
        return false;
      }
    }
    const updated = await performAction(
      enabled ? 'enable_video' : 'disable_video'
    );
    if (!enabled && updated) await mediaSession.stopVideoCapture?.();
    return updated;
  };
  const setVideoOrientation = orientation =>
    performAction('orientation', { orientation });
  const sendReaction = emoji => performAction('reaction', { emoji });
  const setHandRaised = raised =>
    performAction(raised ? 'raise_hand' : 'lower_hand');
  const startScreenShare = async () => {
    try {
      await mediaSession.startVideoCapture?.({ screen: true });
    } catch (captureError) {
      callsStore.setMediaError(errorMessage(captureError));
      return false;
    }
    const started = await performAction('start_screen_share', {
      screen_share_id: 1,
      has_screen_share_id: true,
    });
    if (!started) await mediaSession.stopVideoCapture?.();
    return started;
  };
  const stopScreenShare = async () => {
    const stopped = await performAction('stop_screen_share');
    if (stopped) await mediaSession.stopVideoCapture?.();
    return stopped;
  };
  const addParticipant = target => performAction('add_participant', { target });
  const ringParticipant = target =>
    performAction('ring_participant', { target });
  const setApprovalRequired = enabled =>
    performAction('set_approval_required', { enabled });
  const admitWaitingUser = user =>
    performAction('admit_waiting_user', { user });
  const denyWaitingUser = user => performAction('deny_waiting_user', { user });
  const setVideoCanvas = canvas => mediaSession.setVideoCanvas?.(canvas);

  const cleanup = () => {
    stopQrPolling();
    stopCallPolling();
    gatewayRequestGeneration += 1;
    disconnectMedia();
    callLinkResult.value = null;
    callsStore.reset();
  };

  if (getCurrentInstance()) onUnmounted(cleanup);

  return {
    call,
    status,
    conversation,
    connections,
    connection,
    connectionId,
    isIncoming,
    isStarting,
    isPerformingAction,
    isLoadingConnections,
    isRefreshingConnection,
    error,
    mediaError,
    loadConnections,
    startCall,
    receiveIncomingCall,
    hydrateIncomingCall,
    answerCall,
    rejectCall,
    endCall,
    setMuted,
    startVideo,
    acceptVideo,
    stopVideo,
    setVideoEnabled,
    setVideoOrientation,
    sendReaction,
    setHandRaised,
    startScreenShare,
    stopScreenShare,
    addParticipant,
    ringParticipant,
    setApprovalRequired,
    admitWaitingUser,
    denyWaitingUser,
    setVideoCanvas,
    sendControl,
    refreshConnection,
    updateConnection,
    stopQrPolling,
    stopCallPolling,
    cleanup,
    callLinkResult,
    createCallLink,
    previewCallLink,
    joinCallLink,
  };
}
